import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import api, { formatApiError } from '../api';
import { SUPPORT_WHATSAPP_URL } from '../constants';
import LpAiMessageBody from '../components/LpAiMessageBody';

const STORAGE_KEY = 'lp_gosnel_ai_conversation_id';

export default function AiPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [supportUrl, setSupportUrl] = useState(SUPPORT_WHATSAPP_URL);
  const [apiMissing, setApiMissing] = useState(false);
  const [conversationId, setConversationId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const bottomRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/vendor/lp-ai/support');
        const wa =
          res.data?.whatsapp_url ||
          res.data?.url ||
          (res.data?.whatsapp
            ? `https://wa.me/${String(res.data.whatsapp).replace(/\D/g, '')}`
            : null);
        if (wa) setSupportUrl(wa);
      } catch {
        /* keep default */
      }
    })();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async () => {
    const message = input.trim();
    if (!message || sending) return;
    setError('');
    setSending(true);
    const nextHistory = [...messages, { role: 'user', content: message }];
    setMessages(nextHistory);
    setInput('');
    try {
      const res = await api.post('/api/vendor/lp-ai/chat', {
        message,
        history: nextHistory.map((m) => ({ role: m.role, content: m.content })),
        conversation_id: conversationId || undefined,
      });
      setApiMissing(false);
      const reply =
        res.data?.reply ||
        res.data?.message ||
        res.data?.content ||
        'Sorry — I could not generate a reply.';
      const cid = res.data?.conversation_id || res.data?.id;
      if (cid) {
        setConversationId(cid);
        try {
          localStorage.setItem(STORAGE_KEY, cid);
        } catch {
          /* ignore */
        }
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      if (err?.response?.status === 404) {
        setApiMissing(true);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content:
              'GoSnel AI for Local Partners is not live on the API yet. Use WhatsApp Partner Support below in the meantime.',
          },
        ]);
      } else {
        setError(formatApiError(err, 'Chat failed'));
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Something went wrong. Try again, or chat with Partner Support on WhatsApp.',
          },
        ]);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: '70dvh', md: 560 } }}>
      <Typography variant="h5" fontWeight={900}>
        GoSnel AI
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Ask about products, Neighbour orders, ads, or shop settings. For account/billing issues, use
        WhatsApp support.
      </Typography>

      {apiMissing && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
          AI chat endpoint is not available yet. WhatsApp support still works.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          borderRadius: 2,
          p: 1.5,
          mb: 1.5,
          overflowY: 'auto',
          bgcolor: '#f8fafc',
          minHeight: 280,
        }}
      >
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            Try: “How do walk-in QR codes work?” or “How do I promote a product on Neighbour?”
          </Typography>
        )}
        <Stack spacing={1.25}>
          {messages.map((m, i) => (
            <Box
              key={`${m.role}-${i}`}
              sx={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '92%',
                bgcolor: m.role === 'user' ? '#0f766e' : '#fff',
                color: m.role === 'user' ? '#fff' : 'text.primary',
                border: m.role === 'user' ? 'none' : '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 1.1,
                borderRadius: 2,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <LpAiMessageBody
                content={m.content}
                invert={m.role === 'user'}
                conversationId={conversationId}
              />
            </Box>
          ))}
          {sending && (
            <Box sx={{ alignSelf: 'flex-start', px: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          <div ref={bottomRef} />
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask GoSnel AI…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={send}
          disabled={sending || !input.trim()}
          sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
          aria-label="Send"
        >
          {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="contained"
          color="success"
          startIcon={<WhatsAppIcon />}
          href={supportUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Chat with GoSnel Partner Support
        </Button>
        <Button component={RouterLink} to="/settings" variant="outlined">
          Settings & walk-in QR
        </Button>
      </Stack>
    </Box>
  );
}
